import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, useForm } from "react-hook-form";
import { DefaultAutocomplete } from "../../../../../../common/components/default_autocomplete";
import { MotivoAfastamentoDropDown } from "../../../../domain/models/motivo_afastamento_dropdown";
import { AfastamentoForm, AfastamentoSchema } from "../../../schemas/Funcionarios_cadastro/afastamento_schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { Afastamento } from "../../../../domain/models/afastamento";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import dayjs from "dayjs";

/**
 * CadastroAfastamentoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedAfastamento: Afastamento | null,
  motivosAfastamento: MotivoAfastamentoDropDown[],
  onSubmit: (form: AfastamentoForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onNewMotivoAfastamento: () => void,
  onClose: () => void,
};

export const CadastroAfastamentoModal = ({
  open,
  saving,
  selectedAfastamento,
  motivosAfastamento,
  onSubmit,
  onOpenChangeHistory,
  onNewMotivoAfastamento,
  onClose,
}: Props) => {
  const hookForm = useForm<AfastamentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AfastamentoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedAfastamento) {
        hookForm.setValue('idMotivoAfastamento', selectedAfastamento.idMotivoAfastamento);
        hookForm.setValue('observacao', selectedAfastamento.observacao);

        if (selectedAfastamento.dataInicio) {
          hookForm.setValue('dataInicio', selectedAfastamento.dataInicio);
        } else {
          hookForm.resetField('dataInicio');
        }

        if (selectedAfastamento.dataTermino) {
          hookForm.setValue('dataTermino', selectedAfastamento.dataTermino);
        } else {
          hookForm.resetField('dataTermino');
        }
      } else {
        hookForm.reset();
      }
    }
  }, [
    open,
    selectedAfastamento,
    hookForm,
  ]);

  const handleSubmit = useCallback((form: AfastamentoForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleChangeHistory = useCallback(() => {
    if (selectedAfastamento) {
      onOpenChangeHistory(selectedAfastamento.idAfastamento, LogTipoTabela.FUNCIONARIO_AFASTAMENTO);
    }
  }, [selectedAfastamento, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar Afastamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <Controller
                name='idMotivoAfastamento'
                control={hookForm.control}
                render={({ field }) => (
                  <DefaultAutocomplete
                    label='Motivo Afastamento'
                    options={motivosAfastamento}
                    required={true}
                    getOptionLabel={(opt) => opt.descricao}
                    getOptionKey={(opt) => opt.idMotivoAfastamento}
                    value={motivosAfastamento.find((x) => x.idMotivoAfastamento === field.value)}
                    readOnly={saving}
                    onChange={(newValue) => field.onChange(newValue?.idMotivoAfastamento)} />
                )} />

              <Controller
                name='dataInicio'
                control={hookForm.control}
                render={({ field }) => (
                  <DatePicker
                    readOnly={saving}
                    slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                    label="Data de Início"
                    value={field.value ? dayjs.utc(field.value as any).local() : null}
                    onChange={e => field.onChange(e !== null ? e.startOf('day').utc().toDate() : null)} />
                )} />

              <Controller
                name='dataTermino'
                control={hookForm.control}
                render={({ field }) => (
                  <DatePicker
                    readOnly={saving}
                    slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                    label="Data de Término"
                    value={field.value ? dayjs.utc(field.value as any).local() : null}
                    onChange={e => field.onChange(e !== null ? e.startOf('day').utc().toDate() : null)} />
                )} />

              <DefaultFormInput
                name='observacao'
                label='Observação'
                readOnly={saving}
                multiline={true}
                rows={3}
                reactHookForm={hookForm}
              />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedAfastamento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}